import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Label, Input, Spinner } from 'reactstrap';
import DataTable from 'react-data-table-component';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import { toast } from 'react-toastify';
import ActionButton from '../../../../Components/Common/ActionButton';
import { fetchCduList, fetchEvaluationDocuments } from './registrationApi';
import {
  TECHNICAL_STATUS_OPTIONS,
  PLANT_TEST_STATUS_OPTIONS,
  EVALUATION_TYPE,
} from './registrationConstants';
import { formatForFlatpickr } from '../../../../utils/dateformatpickr';
import DocumentSectionTable from './DocumentSectionTable';
import AddDocumentModal from './AddDocumentModal';

// TMT Berlaku (masaBerlaku) is a Month/Year-only field in view/edit/create
// (rule-crude-registration.md §Tab Implementation) but the backend still
// stores/expects a full date — the day is always normalized to the 1st,
// both for the picker's own onChange and defensively for a value seeded
// from an existing assay (mapDetailToForm passes the BE day through as-is).
// Exported so the payload builders (RegistrationCreateView.js,
// AssayEditorSection.js) can apply the same normalization right before
// formatForAPI, without needing the user to re-touch the picker.
export const toFirstOfMonth = date => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

// Tab 3 — Implementation. Two tables (CDU Unit + Attachment), TMT Berlaku, Notes
// and the final Submit (rule §Tab Implementation). The CDU table uses grouped
// headers (Technical Evaluation / Plant Test) so it's a plain HTML table with
// inline-editable cells; the values live in the shell's form.implementations
// keyed by cduId. Attachment sections reuse the same Evaluation Document master
// + document upload mechanism as Tab 2 (contract: same CrudeDocument, keyed by
// evaluationId).
const ImplementationTab = ({
  form,
  setForm,
  documentsByEval,
  onAddDocument,
  onDeleteDocument,
  onSubmit,
  onBack,
  savingDoc,
  flushing,
  submitting,
  readOnly,
  onDownload,
  implErrors = {},
  setImplErrors,
}) => {
  const [cduList, setCduList] = useState([]);
  const [attachmentSections, setAttachmentSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeEvalId, setActiveEvalId] = useState(null);

  const loadLookups = useCallback(async () => {
    setLoading(true);
    try {
      // Tab 3 attachments are the fixed Implementation-seed rows — pass the
      // explicit evaluationType so the BE doesn't fall back to Tab 2's checklist.
      const [cdus, sections] = await Promise.all([
        fetchCduList(),
        fetchEvaluationDocuments(EVALUATION_TYPE.IMPLEMENTATION),
      ]);
      setCduList(cdus);
      setAttachmentSections(sections);
    } catch (error) {
      toast.error(`Failed to load implementation data: ${error}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const impls = form.implementations || {};

  // Patch one field of a CDU's implementation row, tagging cduName for the
  // all-or-nothing validation messages.
  const setImplField = (cdu, key, value) => {
    setForm(prev => {
      const next = { ...(prev.implementations || {}) };
      next[cdu.id] = {
        ...next[cdu.id],
        cduId: cdu.id,
        cduName: cdu.cduName,
        [key]: value,
      };
      return { ...prev, implementations: next };
    });
    // Clear this cell's error highlight as soon as it's edited (removing the row
    // entry when no flagged cells remain).
    if (setImplErrors && implErrors?.[cdu.id]?.[key]) {
      setImplErrors(prev => {
        const row = { ...(prev[cdu.id] || {}) };
        delete row[key];
        const next = { ...prev };
        if (Object.keys(row).length === 0) delete next[cdu.id];
        else next[cdu.id] = row;
        return next;
      });
    }
  };

  const openAdd = evaluationId => {
    setActiveEvalId(evaluationId);
    setModalOpen(true);
  };

  const handleSaveDocument = payload => {
    // Local staging only — the file uploads on Submit, not here.
    onAddDocument({ ...payload, evaluationId: activeEvalId });
    setModalOpen(false);
  };

  const ExpandedSection = ({ data }) => (
    <DocumentSectionTable
      documents={documentsByEval[data.id] || []}
      disabled={savingDoc || flushing}
      readOnly={readOnly}
      onAdd={() => openAdd(data.id)}
      onDelete={row => onDeleteDocument(data.id, row)}
      onDownload={onDownload}
    />
  );

  const attachmentColumns = [
    {
      name: <span className='text-uppercase fw-semibold'>Attachment</span>,
      selector: row => row.evaluasiName,
      wrap: true,
    },
  ];

  return (
    <div>
      {loading ? (
        <div className='text-center py-4'>
          <Spinner size='sm' /> Loading...
        </div>
      ) : (
        <>
          {/* ---------- CDU Unit table ---------- */}
          <div className='table-responsive mb-4'>
            <table className='table table-bordered align-middle mb-0'>
              <thead className='table-primary'>
                <tr>
                  <th rowSpan={2} className='align-middle'>
                    CD Unit
                  </th>
                  <th colSpan={3} className='text-center'>
                    Technical Evaluation
                  </th>
                  <th colSpan={3} className='text-center'>
                    Plant Test
                  </th>
                </tr>
                <tr>
                  <th>Status</th>
                  <th>Ratio (%-Vol)</th>
                  <th>Limit (MB/Month)</th>
                  <th>Status</th>
                  <th>Ratio (%-Vol)</th>
                  <th>Limit (MB/Month)</th>
                </tr>
              </thead>
              <tbody>
                {cduList.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-center text-muted py-3'>
                      No CDU found
                    </td>
                  </tr>
                )}
                {cduList.map(cdu => {
                  const row = impls[cdu.id] || {};
                  return (
                    <tr key={cdu.id}>
                      <td>{cdu.cduName}</td>
                      <td>
                        <Input
                          type='select'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.technicalStatus}
                          value={row.technicalStatus ?? ''}
                          onChange={e =>
                            setImplField(cdu, 'technicalStatus', e.target.value)
                          }
                        >
                          <option value=''>-</option>
                          {TECHNICAL_STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input
                          type='number'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.technicalRatio}
                          value={row.technicalRatio ?? ''}
                          onChange={e =>
                            setImplField(cdu, 'technicalRatio', e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Input
                          type='number'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.technicalLimit}
                          value={row.technicalLimit ?? ''}
                          onChange={e =>
                            setImplField(cdu, 'technicalLimit', e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Input
                          type='select'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.implementationStatus}
                          value={row.implementationStatus ?? ''}
                          onChange={e =>
                            setImplField(
                              cdu,
                              'implementationStatus',
                              e.target.value
                            )
                          }
                        >
                          <option value=''>-</option>
                          {PLANT_TEST_STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input
                          type='number'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.implementationRatio}
                          value={row.implementationRatio ?? ''}
                          onChange={e =>
                            setImplField(
                              cdu,
                              'implementationRatio',
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <Input
                          type='number'
                          bsSize='sm'
                          disabled={readOnly}
                          invalid={!!implErrors?.[cdu.id]?.implementationLimit}
                          value={row.implementationLimit ?? ''}
                          onChange={e =>
                            setImplField(
                              cdu,
                              'implementationLimit',
                              e.target.value
                            )
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---------- Attachment table ---------- */}
          <Row>
            <Col lg={7}>
              <DataTable
                columns={attachmentColumns}
                data={attachmentSections}
                expandableRows
                expandableRowsComponent={ExpandedSection}
                dense
                striped
                noDataComponent={
                  <div className='py-3 text-muted'>No attachment sections</div>
                }
              />
            </Col>
            <Col lg={5}>
              <div className='mb-3'>
                <Label className='form-label'>TMT Berlaku (Year/Month)</Label>
                {readOnly ? (
                  // react-flatpickr has no disabled/readOnly prop — render the
                  // value as plain read-only text instead.
                  <Input
                    type='text'
                    readOnly
                    value={
                      form.masaBerlaku
                        ? new Intl.DateTimeFormat('en-GB', {
                            month: 'long',
                            year: 'numeric',
                          }).format(new Date(form.masaBerlaku))
                        : ''
                    }
                  />
                ) : (
                  <Flatpickr
                    className='form-control'
                    value={formatForFlatpickr(form.masaBerlaku)}
                    options={{
                      dateFormat: 'Y-m',
                      plugins: [
                        new monthSelectPlugin({
                          shorthand: false,
                          dateFormat: 'Y-m',
                          altFormat: 'F Y',
                          theme: 'light',
                        }),
                      ],
                    }}
                    onChange={([d]) =>
                      setForm(prev => ({
                        ...prev,
                        masaBerlaku: toFirstOfMonth(d),
                      }))
                    }
                  />
                )}
              </div>
              <div className='mb-3'>
                <Label className='form-label'>Notes</Label>
                <Input
                  type='textarea'
                  rows={3}
                  disabled={readOnly}
                  value={form.implementationNote || ''}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      implementationNote: e.target.value,
                    }))
                  }
                />
              </div>
            </Col>
          </Row>
        </>
      )}

      {!readOnly && (
        <div className='d-flex justify-content-between mt-4'>
          <ActionButton
            variant='secondary'
            icon='ri-arrow-left-line'
            onClick={onBack}
          >
            Prev
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-save-line'
            disabled={submitting || flushing}
            onClick={onSubmit}
          >
            {flushing
              ? 'Uploading...'
              : submitting
                ? 'Submitting...'
                : 'Submit'}
          </ActionButton>
        </div>
      )}

      <AddDocumentModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        onSave={handleSaveDocument}
      />
    </div>
  );
};

ImplementationTab.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  documentsByEval: PropTypes.object.isRequired,
  onAddDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  savingDoc: PropTypes.bool,
  flushing: PropTypes.bool,
  submitting: PropTypes.bool,
  readOnly: PropTypes.bool,
  onDownload: PropTypes.func,
  implErrors: PropTypes.object,
  setImplErrors: PropTypes.func,
};

export default ImplementationTab;
